import { db } from './firebase';
import {
  collection, getDocs, addDoc, serverTimestamp
} from 'firebase/firestore';
import { buildEmptyProfile, withPropagationMarker } from './profileDefaults';

// Mutualise les appels concurrents pour un même uid. Plusieurs flux d'auth
// (loginWithEmail + onAuthStateChanged, etc.) déclenchent la migration quasi
// simultanément ; sans ce cache, chaque appel lit "empty" puis écrit, ce qui
// produit des doublons (constat: 2 docs "Moi" créés à 12 ms d'écart).
const inFlight = new Map();

// Vraies préférences legacy ? Un compte tout neuf a preferences = { regime: 'Omnivore' }
// par défaut : ce n'est PAS du legacy. On ne parle de "préférences reprises" que
// s'il y a un régime non-Omnivore, au moins une allergie, ou des aversions.
function hasMeaningfulLegacy(prefs) {
  if (!prefs || typeof prefs !== 'object') return false;
  const regime = String(prefs.regime || '').trim().toLowerCase();
  const hasRegime = regime && regime !== 'omnivore';
  const hasAllergies = prefs.allergies && typeof prefs.allergies === 'object'
    && Object.values(prefs.allergies).some(Boolean);
  const hasAversions = typeof prefs.aversions === 'string' && prefs.aversions.trim().length > 0;
  return !!(hasRegime || hasAllergies || hasAversions);
}

async function _doMigration(uid, legacyPreferences, opts) {
  const colRef = collection(db, 'users', uid, 'profiles');
  const snap = await getDocs(colRef);
  if (!snap.empty) {
    return { migrated: false };
  }

  const prefs = legacyPreferences || {};
  const isLegacy = hasMeaningfulLegacy(prefs);
  const profileName = (opts.name || '').trim() || 'Moi';
  const profile = buildEmptyProfile({
    name: profileName,
    regime: prefs.regime || 'Omnivore',
    allergies: prefs.allergies && typeof prefs.allergies === 'object' ? { ...prefs.allergies } : {},
    aversions: typeof prefs.aversions === 'string' ? prefs.aversions : '',
    isDefault: true
  });

  const ref = await addDoc(colRef, {
    // Marqueur propagationFree : profil créé par le client actuel (cf. withPropagationMarker).
    ...withPropagationMarker(profile),
    // _migratedFrom seulement pour de vraies prefs legacy ; _origin pour distinguer
    // compte neuf vs migré (copy d'onboarding différente).
    _migratedFrom: isLegacy ? 'legacy_preferences' : null,
    _origin: isLegacy ? 'legacy' : 'new_signup',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return { migrated: true, profileId: ref.id, isLegacy };
}

/**
 * Migration paresseuse : si l'utilisateur n'a aucun profil dans
 * `users/{uid}/profiles`, on en crée un (par défaut nommé "Moi", ou le prénom
 * passé en option) rempli avec ses `users/{uid}.preferences` legacy.
 *
 * Idempotent et safe contre les appels concurrents pour un même uid.
 *
 * @param {string} uid
 * @param {object} legacyPreferences - users/{uid}.preferences (peut être null/undefined)
 * @param {object} [opts]
 * @param {string} [opts.name] - Prénom à utiliser à la place de "Moi"
 * @returns {Promise<{ migrated: boolean, profileId?: string }>}
 */
export async function migrateLegacyToProfile(uid, legacyPreferences, opts = {}) {
  if (!db || !uid) return { migrated: false };

  if (inFlight.has(uid)) {
    return inFlight.get(uid);
  }

  const promise = _doMigration(uid, legacyPreferences, opts).finally(() => {
    inFlight.delete(uid);
  });
  inFlight.set(uid, promise);
  return promise;
}
